/**
 * 1 Nov 2026 (D-1102-CREW-1): the device keeps the last Today it loaded, so the screen has something to show the
 * moment the app opens while the live read is on its way.
 *
 * Rules (each one has a test):
 *   - one copy, for ONE signed-in person and ONE service day — a copy for another person or another day is never
 *     shown (and is dropped when it is met);
 *   - only what Today already shows: the tenants' phone numbers, emails and contact people are taken out before it is
 *     written, so nothing here can place a call or send anything;
 *   - every read and write is in try/catch — private windows and blocked storage simply mean no stored copy;
 *   - cleared on sign-out (and whenever the sign-in page opens);
 *   - it is a picture, not data to act on: Today's buttons wait for the live read while the stored copy is on screen.
 */
import type { TodayResponse, TodayVisit } from '@shared/api-types';

export const TODAY_CACHE_KEY = 'abf.todayCopy.v1';

export interface TodayCopy {
  user: string;
  day: string;
  /** When the live read it came from arrived (ms). */
  savedAt: number;
  data: TodayResponse;
}

const userKey = (user: string | null | undefined) => String(user ?? '').trim().toLowerCase();

/** The visit as Today shows it: no phone, no email, no contact person's number. */
function plain(v: TodayVisit): TodayVisit {
  const { email: _email, callFirst: _callFirst, ...rest } = v;
  return { ...rest, phone: null, email: null, callFirst: null };
}

/** The copy that is stored: the day as read, less every number and address. */
export function copyOf(data: TodayResponse): TodayResponse {
  return { ...data, visits: data.visits.map(plain) };
}

export function writeTodayCopy(user: string | null | undefined, day: string, data: TodayResponse, savedAt = Date.now()): void {
  const who = userKey(user);
  if (!who || !day || data.day !== day || data.past) return;
  try {
    const copy: TodayCopy = { user: who, day, savedAt, data: copyOf(data) };
    window.localStorage.setItem(TODAY_CACHE_KEY, JSON.stringify(copy));
  } catch {
    /* no storage here: the screen simply opens the way it always did */
  }
}

export function readTodayCopy(user: string | null | undefined, day: string): TodayCopy | null {
  const who = userKey(user);
  if (!who || !day) return null;
  try {
    const raw = window.localStorage.getItem(TODAY_CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<TodayCopy> | null;
    const ok =
      !!c && typeof c.user === 'string' && typeof c.day === 'string' && typeof c.savedAt === 'number' && !!c.data && Array.isArray(c.data.visits) && c.data.day === c.day;
    if (!ok) {
      clearTodayCopy();
      return null;
    }
    // another person's, or another day's: never shown, and not kept
    if (c.user !== who || c.day !== day) {
      clearTodayCopy();
      return null;
    }
    return c as TodayCopy;
  } catch {
    return null;
  }
}

export function clearTodayCopy(): void {
  try {
    window.localStorage.removeItem(TODAY_CACHE_KEY);
  } catch {
    /* nothing stored, or no storage */
  }
}
